import React, { useMemo, useState, createElement } from 'react';
import { useData } from '../../contexts/DataContext';
import { formatDate } from '../../utils/mockData';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { CalendarIcon, FilterIcon, DownloadIcon } from 'lucide-react';
const HistoricalDataPage: React.FC = () => {
  const {
    readings,
    threshold,
    exportData
  } = useData();
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Filter readings by selected time range or custom date range
  const filteredReadings = useMemo(() => {
    const now = new Date();
    let filterStartDate: Date;
    if (startDate && endDate) {
      // Custom date range
      filterStartDate = new Date(startDate);
      const filterEndDate = new Date(endDate);
      filterEndDate.setHours(23, 59, 59, 999); // End of the day
      return readings.filter(reading => {
        const readingDate = new Date(reading.timestamp);
        return readingDate >= filterStartDate && readingDate <= filterEndDate;
      });
    } else {
      // Predefined time range
      switch (timeRange) {
        case 'day':
          filterStartDate = new Date(now);
          filterStartDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterStartDate = new Date(now);
          filterStartDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterStartDate = new Date(now);
          filterStartDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          filterStartDate = new Date(now);
          filterStartDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          filterStartDate = new Date(now);
          filterStartDate.setMonth(now.getMonth() - 1);
      }
      return readings.filter(reading => new Date(reading.timestamp) >= filterStartDate);
    }
  }, [readings, timeRange, startDate, endDate]);
  // Format data for chart based on time range
  const chartData = useMemo(() => {
    if (filteredReadings.length === 0) return [];
    // If showing a day or less, show hourly data
    if (timeRange === 'day' || startDate && endDate && new Date(endDate).getTime() - new Date(startDate).getTime() <= 86400000) {
      return filteredReadings.map(reading => {
        const date = new Date(reading.timestamp);
        return {
          time: date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          }),
          fullTime: date.toLocaleString(),
          value: reading.value
        };
      });
    }
    // If showing a week, show daily data
    if (timeRange === 'week' || startDate && endDate && new Date(endDate).getTime() - new Date(startDate).getTime() <= 604800000) {
      // Group by day
      const dailyData: Record<string, number[]> = {};
      filteredReadings.forEach(reading => {
        const date = new Date(reading.timestamp);
        const dayKey = date.toLocaleDateString();
        if (!dailyData[dayKey]) {
          dailyData[dayKey] = [];
        }
        dailyData[dayKey].push(reading.value);
      });
      return Object.entries(dailyData).map(([day, values]) => ({
        time: day,
        fullTime: day,
        value: values.reduce((sum, val) => sum + val, 0) / values.length
      }));
    }
    // If showing a month or more, show weekly data
    const weeklyData: Record<string, number[]> = {};
    filteredReadings.forEach(reading => {
      const date = new Date(reading.timestamp);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      const weekKey = weekStart.toLocaleDateString();
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = [];
      }
      weeklyData[weekKey].push(reading.value);
    });
    return Object.entries(weeklyData).map(([week, values]) => ({
      time: `Week of ${week}`,
      fullTime: `Week of ${week}`,
      value: values.reduce((sum, val) => sum + val, 0) / values.length
    }));
  }, [filteredReadings, timeRange, startDate, endDate]);
  // Handle exporting filtered data
  const handleExportFiltered = () => {
    // Create CSV content for filtered data
    const headers = 'Timestamp,Value (kWh)\n';
    const rows = filteredReadings.map(r => `${r.timestamp},${r.value}`).join('\n');
    const csvContent = `data:text/csv;charset=utf-8,${headers}${rows}`;
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `energy_readings_filtered_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    // Trigger download
    link.click();
    document.body.removeChild(link);
  };
  return <div className="space-y-6" data-id="element-169">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-170">
        <h2 className="text-2xl font-bold" data-id="element-171">Historical Data</h2>
        <div className="mt-3 md:mt-0 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2" data-id="element-172">
          <button onClick={exportData} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors" data-id="element-173">
            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-174" />
            Export All Data
          </button>
          <button onClick={handleExportFiltered} className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors" data-id="element-175">
            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-176" />
            Export Filtered Data
          </button>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-177">
        <div className="flex items-center mb-4" data-id="element-178">
          <FilterIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-179" />
          <h3 className="text-lg font-semibold" data-id="element-180">Filter Data</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-181">
          <div data-id="element-182">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-183">
              Time Range
            </h4>
            <div className="flex space-x-2" data-id="element-184">
              <button onClick={() => {
              setTimeRange('day');
              setStartDate('');
              setEndDate('');
            }} className={`px-3 py-1 rounded-md text-sm ${timeRange === 'day' && !startDate && !endDate ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-185">
                Day
              </button>
              <button onClick={() => {
              setTimeRange('week');
              setStartDate('');
              setEndDate('');
            }} className={`px-3 py-1 rounded-md text-sm ${timeRange === 'week' && !startDate && !endDate ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-186">
                Week
              </button>
              <button onClick={() => {
              setTimeRange('month');
              setStartDate('');
              setEndDate('');
            }} className={`px-3 py-1 rounded-md text-sm ${timeRange === 'month' && !startDate && !endDate ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-187">
                Month
              </button>
              <button onClick={() => {
              setTimeRange('year');
              setStartDate('');
              setEndDate('');
            }} className={`px-3 py-1 rounded-md text-sm ${timeRange === 'year' && !startDate && !endDate ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-188">
                Year
              </button>
            </div>
          </div>
          <div data-id="element-189">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-190">
              Custom Date Range
            </h4>
            <div className="flex items-center space-x-2" data-id="element-191">
              <div className="relative flex-1" data-id="element-192">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-193">
                  <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-194" />
                </div>
                <input type="date" className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={startDate} onChange={e => setStartDate(e.target.value)} data-id="element-195" />
              </div>
              <span className="text-gray-500" data-id="element-196">to</span>
              <div className="relative flex-1" data-id="element-197">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-198">
                  <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-199" />
                </div>
                <input type="date" className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate} data-id="element-200" />
              </div>
              <button onClick={() => {
              setStartDate('');
              setEndDate('');
              setTimeRange('month');
            }} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" data-id="element-201">
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Chart */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-202">
        <h3 className="text-lg font-semibold mb-4" data-id="element-203">
          Energy Consumption History
        </h3>
        {chartData.length === 0 ? <div className="h-80 flex items-center justify-center text-gray-500" data-id="element-204">
            No data available for the selected time range
          </div> : <div className="h-80" data-id="element-205">
            <ResponsiveContainer width="100%" height="100%" data-id="element-206">
              <LineChart data={chartData} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-207">
                <CartesianGrid strokeDasharray="3 3" data-id="element-208" />
                <XAxis dataKey="time" angle={-45} textAnchor="end" height={70} tick={{
              fontSize: 12
            }} data-id="element-209" />
                <YAxis data-id="element-210" />
                <Tooltip formatter={(value, name) => [`${Number(value).toFixed(2)} kWh`, 'Energy']} labelFormatter={label => chartData.find(d => d.time === label)?.fullTime || label} data-id="element-211" />
                <ReferenceLine y={threshold} stroke="red" strokeDasharray="3 3" label={{
              value: `Threshold (${threshold} kWh)`,
              position: 'insideTopRight',
              fill: 'red'
            }} data-id="element-212" />
                <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{
              r: 3
            }} activeDot={{
              r: 5
            }} data-id="element-213" />
              </LineChart>
            </ResponsiveContainer>
          </div>}
      </div>
      {/* Data Table */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-214">
        <h3 className="text-lg font-semibold mb-4" data-id="element-215">Data Table</h3>
        <div className="overflow-x-auto" data-id="element-216">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-217">
            <thead className="bg-gray-50" data-id="element-218">
              <tr data-id="element-219">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-220">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-221">
                  Value (kWh)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-222">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-223">
              {filteredReadings.slice(0, 50).map(reading => <tr key={reading.id} data-id="element-224">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-225">
                    {formatDate(reading.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-226">
                    {reading.value.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-227">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${reading.value < threshold ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`} data-id="element-228">
                      {reading.value < threshold ? 'Below Threshold' : 'Normal'}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
          {filteredReadings.length > 50 && <div className="py-3 text-center text-sm text-gray-500" data-id="element-229">
              Showing 50 of {filteredReadings.length} records. Export data to
              view all.
            </div>}
          {filteredReadings.length === 0 && <div className="py-8 text-center text-gray-500" data-id="element-230">
              No data available for the selected time range
            </div>}
        </div>
      </div>
    </div>;
};
export default HistoricalDataPage;